import React, { useMemo, useState, useRef, useCallback } from 'react';

/**
 * <AutomationFlow /> — the visual automation map.
 *
 * Sam has wanted a proper visual automation library for a while, but Josh raised
 * ownership concerns about the off-the-shelf flow libraries (the good ones put the
 * useful half behind a commercial licence). So this is written from scratch: plain
 * SVG, no dependencies, nothing to license.
 *
 * It reads the rules that already exist — form rules and job rules both — and lays
 * them out as trigger → condition → action. The point isn't decoration: seeing them
 * side by side is what makes two problems visible that a list hides completely —
 * two rules fighting over the same field off one trigger, and a rule whose action
 * fires its own trigger again. Both are called out on the canvas.
 */

const COL = { trigger: 0, condition: 1, action: 2 };
const NODE_W = 210;
const NODE_H = 62;
const GAP_X = 130;
const GAP_Y = 22;
const PAD = 28;

const TRIGGER_LABEL = {
  job_created: 'Job created',
  job_status_changed: 'Job status changed',
  job_completed: 'Job completed',
  invoice_raised: 'Invoice raised',
  submit: 'Form submitted',
  on_submit: 'Form submitted',
  // Retired form triggers — kept so old rules are labelled honestly.
  status: 'Form status set (never runs)',
  on_set_status: 'Form status set (never runs)',
  save: 'Form saved (never runs)',
  on_save: 'Form saved (never runs)',
};
const ACTION_LABEL = {
  notify_fm: 'Notify the client',
  set_job_status: 'Set job status',
  auto_raise_invoice: 'Raise a draft invoice',
};
/** Which trigger does an action itself cause? This is how loops are spotted. */
/** What a form rule's action does (form rules store the action id in `action`). */
const FORM_ACTION_LABEL = {
  notify: 'Notify the client',
  send_email: 'Email the client',
  set_job_status: 'Set job status',
  create_job: 'Raise a remedial job',
  create_data: 'Create data (not available)',
  edit_data: 'Edit data (not available)',
};
const ACTION_FIRES = {
  set_job_status: 'job_status_changed',
  auto_raise_invoice: 'invoice_raised',
};
/** Actions that write the same field can't both win. */
const ACTION_WRITES = { set_job_status: 'job.status' };

const PALETTE = {
  trigger: { bg: '#eff6ff', bd: '#bfdbfe', fg: '#1565C0', icon: 'fa-bolt', dot: '#3b82f6' },
  condition: { bg: '#fffbeb', bd: '#fde68a', fg: '#92400e', icon: 'fa-code-branch', dot: '#f59e0b' },
  action: { bg: '#f0fdf4', bd: '#bbf7d0', fg: '#166534', icon: 'fa-play', dot: '#22c55e' },
};

const shortNum = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

/**
 * Turn both rule families into one graph.
 * Rules that share a trigger share its node — that shared node is exactly where a
 * conflict becomes obvious.
 */
export function buildGraph(jobRules = [], formRules = []) {
  const nodes = [];
  const edges = [];
  const triggerIdx = new Map();

  const triggerNode = (key, label) => {
    if (triggerIdx.has(key)) return triggerIdx.get(key);
    const n = { id: `t:${key}`, kind: 'trigger', key, label, rules: [] };
    nodes.push(n); triggerIdx.set(key, n);
    return n;
  };

  const add = (rule, source) => {
    const tKey = source === 'job' ? rule.trigger_event : (rule.trigger_on || 'submit');
    const t = triggerNode(tKey, TRIGGER_LABEL[tKey] || tKey);
    t.rules.push(rule.id);

    // A rule only gets a condition node when it actually narrows something —
    // an empty "when anything happens" box is noise on a diagram.
    const condText = source === 'job'
      ? [rule.from_status && `from ${rule.from_status}`, rule.to_status && `to ${rule.to_status}`].filter(Boolean).join(' ')
      : (rule.condition_text || '');
    let prev = t;
    if (condText) {
      const c = { id: `c:${source}:${rule.id}`, kind: 'condition', label: condText, ruleId: rule.id, source, active: rule.is_active !== 0 };
      nodes.push(c);
      edges.push({ from: prev.id, to: c.id, ruleId: rule.id, source, active: c.active, runs: rule.run_count || 0 });
      prev = c;
    }

    const aKey = source === 'job' ? rule.action : 'form_action';
    const a = {
      id: `a:${source}:${rule.id}`, kind: 'action', ruleId: rule.id, source,
      label: source === 'job' ? (ACTION_LABEL[aKey] || aKey) : (FORM_ACTION_LABEL[rule.action] || 'Form action'),
      detail: rule.description || '',
      actionKey: aKey,
      active: rule.is_active !== 0,
      runs: rule.run_count || 0,
    };
    nodes.push(a);
    edges.push({ from: prev.id, to: a.id, ruleId: rule.id, source, active: a.active, runs: rule.run_count || 0 });
  };

  jobRules.forEach((r) => add(r, 'job'));
  formRules.forEach((r) => add(r, 'form'));

  const warnings = [];
  const actions = nodes.filter((n) => n.kind === 'action');

  // An action can itself raise a trigger. Draw that as a feedback edge so the
  // round trip is visible rather than implied.
  actions.forEach((a) => {
    const fires = ACTION_FIRES[a.actionKey];
    const t = fires && triggerIdx.get(fires);
    if (t) edges.push({ from: a.id, to: t.id, feedback: true, active: a.active });
  });

  // ── Loops ──
  // Walk trigger → action → trigger and look for a cycle. A direct self-loop is the
  // common case, but the dangerous ones are longer: complete a job → raise an
  // invoice → set the status → which completes a job. A list of rules cannot show
  // that; this is the main reason the map is worth drawing at all.
  const firesFrom = new Map();                    // trigger key → [{action, trigger key it raises}]
  triggerIdx.forEach((t, key) => {
    const downstream = actions.filter((a) => a.active && reaches(t.id, a.id, edges, nodes));
    firesFrom.set(key, downstream
      .map((a) => ({ action: a, next: ACTION_FIRES[a.actionKey] }))
      .filter((x) => x.next && triggerIdx.has(x.next)));
  });

  const seenCycle = new Set();
  const walk = (key, path, visited) => {
    (firesFrom.get(key) || []).forEach(({ action, next }) => {
      const idx = path.indexOf(next);
      if (idx >= 0) {
        const cycle = [...path.slice(idx), next];
        // Same circle entered at a different point is the same problem — report it once.
        const sig = [...new Set(cycle)].sort().join('>');
        if (seenCycle.has(sig)) return;
        seenCycle.add(sig);
        const names = cycle.map((k) => TRIGGER_LABEL[k] || k);
        warnings.push({
          type: 'loop', nodeId: action.id, ruleId: action.ruleId, source: action.source,
          text: cycle.length <= 2
            ? `"${action.detail || action.label}" fires ${names[0]}, which runs it again — this can loop.`
            : `These run in a circle: ${names.join(' → ')}. "${action.detail || action.label}" closes the loop.`,
        });
        return;
      }
      if (visited.has(next)) return;
      visited.add(next);
      walk(next, [...path, next], visited);
    });
  };
  triggerIdx.forEach((t, key) => walk(key, [key], new Set([key])));

  // ── Conflicts: two live rules writing the same field off one trigger ──
  triggerIdx.forEach((t) => {
    const downstream = actions.filter((a) => a.active && reaches(t.id, a.id, edges, nodes));
    const byField = {};
    downstream.forEach((a) => {
      const f = ACTION_WRITES[a.actionKey];
      if (f) (byField[f] = byField[f] || []).push(a);
    });
    Object.entries(byField).forEach(([field, list]) => {
      if (list.length > 1) {
        warnings.push({ type: 'conflict', nodeId: list[0].id, ruleIds: list.map((a) => a.ruleId),
          text: `${list.length} live rules all set ${field} when ${t.label.toLowerCase()} — the last one to run wins.` });
      }
    });
  });

  return { nodes, edges, warnings };
}

/** Is `actionId` downstream of `triggerId` — directly, or through its condition box? */
function reaches(triggerId, actionId, edges, nodes) {
  if (edges.some((e) => !e.feedback && e.from === triggerId && e.to === actionId)) return true;
  return nodes.some((n) => n.kind === 'condition'
    && edges.some((e) => !e.feedback && e.from === triggerId && e.to === n.id)
    && edges.some((e) => !e.feedback && e.from === n.id && e.to === actionId));
}

/** Column-based layout: triggers left, conditions middle, actions right. */
function layout(nodes) {
  const cols = [[], [], []];
  nodes.forEach((n) => cols[COL[n.kind]].push(n));
  const pos = {};
  cols.forEach((col, ci) => {
    col.forEach((n, ri) => {
      pos[n.id] = { x: PAD + ci * (NODE_W + GAP_X), y: PAD + ri * (NODE_H + GAP_Y) };
    });
  });
  const height = PAD * 2 + Math.max(1, ...cols.map((c) => c.length)) * (NODE_H + GAP_Y);
  const width = PAD * 2 + 3 * NODE_W + 2 * GAP_X;
  return { pos, width, height };
}

/** Horizontal bezier — a straight line through three columns is unreadable. */
const curve = (a, b) => {
  const x1 = a.x + NODE_W; const y1 = a.y + NODE_H / 2;
  const x2 = b.x; const y2 = b.y + NODE_H / 2;
  const dx = Math.max(40, (x2 - x1) / 2);
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
};

/** A feedback edge loops back leftwards, so it needs to route below everything. */
const feedbackCurve = (a, b, bottom) => {
  const x1 = a.x + NODE_W / 2; const y1 = a.y + NODE_H;
  const x2 = b.x + NODE_W / 2; const y2 = b.y + NODE_H;
  const dip = bottom + 26;
  return `M ${x1} ${y1} C ${x1} ${dip}, ${x2} ${dip}, ${x2} ${y2}`;
};

const AutomationFlow = ({ jobRules = [], formRules = [], onSelectRule }) => {
  const [sel, setSel] = useState(null);          // selected node id
  const [zoom, setZoom] = useState(1);
  const wrapRef = useRef(null);

  const graph = useMemo(() => buildGraph(jobRules, formRules), [jobRules, formRules]);
  const { pos, width, height } = useMemo(() => layout(graph.nodes), [graph.nodes]);
  const bottom = height - PAD;

  /** Everything reachable from (or reaching) the selected node, so a click reads as a path. */
  const lit = useMemo(() => {
    if (!sel) return null;
    // Walk outward from the clicked node in both directions until nothing new turns
    // up — a worklist rather than repeated full sweeps over the edge list.
    const keep = new Set([sel]);
    const queue = [sel];
    while (queue.length) {
      const at = queue.pop();
      for (const e of graph.edges) {
        if (e.feedback) continue;
        if (e.from === at && !keep.has(e.to)) { keep.add(e.to); queue.push(e.to); }
        if (e.to === at && !keep.has(e.from)) { keep.add(e.from); queue.push(e.from); }
      }
    }
    return keep;
  }, [sel, graph.edges]);

  const dim = useCallback((id) => (lit && !lit.has(id) ? 0.18 : 1), [lit]);

  const warnByNode = useMemo(() => {
    const m = {};
    graph.warnings.forEach((w) => { (m[w.nodeId] = m[w.nodeId] || []).push(w); });
    return m;
  }, [graph.warnings]);

  if (!graph.nodes.length) {
    return (
      <div style={{ padding: '46px 20px', textAlign: 'center', border: '1px dashed #d1d5db', borderRadius: 12, background: '#fafbfc' }}>
        <i className="fa-solid fa-diagram-project" style={{ fontSize: '1.7rem', color: '#d1d5db' }} />
        <div style={{ fontWeight: 700, color: '#374151', marginTop: 10 }}>No automations to map yet</div>
        <div className="cw-meta" style={{ marginTop: 4 }}>
          Create a rule and it appears here as a flow.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Problems the diagram exists to reveal. */}
      {graph.warnings.length > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '11px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#991b1b', fontWeight: 700, fontSize: '0.83rem' }}>
            <i className="fa-solid fa-triangle-exclamation" />
            {graph.warnings.length} thing{graph.warnings.length === 1 ? '' : 's'} worth checking
          </div>
          <ul style={{ margin: '7px 0 0', paddingLeft: 20, fontSize: '0.79rem', color: '#b91c1c', lineHeight: 1.55 }}>
            {graph.warnings.map((w, i) => (
              <li key={i}>
                <button type="button" onClick={() => setSel(w.nodeId)} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit', textAlign: 'left', cursor: 'pointer', textDecoration: 'underline dotted' }}>
                  {w.text}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Canvas controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 14, marginRight: 'auto', flexWrap: 'wrap' }}>
          {[['trigger', 'When'], ['condition', 'Only if'], ['action', 'Then']].map(([k, label]) => (
            <span key={k} className="cw-meta" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 9, height: 9, borderRadius: 3, background: PALETTE[k].dot }} />{label}
            </span>
          ))}
        </div>
        {sel && (
          <button className="cw-btn cw-btn-secondary" style={{ fontSize: '0.76rem', padding: '0.28rem 0.6rem' }} onClick={() => setSel(null)}>
            <i className="fa-solid fa-xmark" /> Clear highlight
          </button>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button type="button" className="cw-icon-btn" aria-label="Zoom out" title="Zoom out" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}><i className="fa-solid fa-magnifying-glass-minus" aria-hidden="true" /></button>
          <span style={{ fontSize: '0.74rem', color: '#6b7280', minWidth: 38, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
          <button type="button" className="cw-icon-btn" aria-label="Zoom in" title="Zoom in" onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(2)))}><i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" /></button>
          <button type="button" className="cw-icon-btn" aria-label="Reset zoom" title="Reset zoom" onClick={() => setZoom(1)}><i className="fa-solid fa-compress" aria-hidden="true" /></button>
        </div>
      </div>

      <div ref={wrapRef} style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: '#fbfcfd', overflow: 'auto', maxHeight: 560 }}>
        <svg
          width={width * zoom} height={(height + 46) * zoom} viewBox={`0 0 ${width} ${height + 46}`}
          style={{ display: 'block', minWidth: '100%' }}
          role="img" aria-label="Automation flow map"
        >
          <defs>
            <marker id="af-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#9ca3af" />
            </marker>
            <marker id="af-arrow-loop" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#f97316" />
            </marker>
            <pattern id="af-grid" width="22" height="22" patternUnits="userSpaceOnUse">
              <path d="M 22 0 L 0 0 0 22" fill="none" stroke="#eef1f5" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#af-grid)" />

          {/* Edges first so nodes sit on top */}
          {graph.edges.map((e, i) => {
            const a = pos[e.from]; const b = pos[e.to];
            if (!a || !b) return null;
            const on = dim(e.from) === 1 && dim(e.to) === 1;
            if (e.feedback) {
              // Every status-setting action technically feeds a trigger, so drawing
              // them all turns the canvas into spaghetti. Show the round trip only
              // where it matters: on a rule that's been flagged, or on the path
              // you've clicked to trace.
              const worthDrawing = !!warnByNode[e.from] || (lit && lit.has(e.from));
              if (!worthDrawing) return null;
              return (
                <path key={`e${i}`} d={feedbackCurve(a, b, bottom)} fill="none"
                  stroke="#f97316" strokeWidth={1.6} strokeDasharray="5 4"
                  opacity={e.active ? 0.8 : 0.35} markerEnd="url(#af-arrow-loop)" />
              );
            }
            return (
              <g key={`e${i}`} opacity={on ? 1 : 0.1}>
                <path d={curve(a, b)} fill="none" stroke="#cbd5e1"
                  strokeWidth={e.runs > 0 ? Math.min(3.4, 1.4 + Math.log10(e.runs + 1)) : 1.4}
                  strokeDasharray={e.active ? undefined : '4 4'} markerEnd="url(#af-arrow)" />
                {e.runs > 0 && (
                  <text x={(a.x + NODE_W + b.x) / 2} y={(a.y + b.y) / 2 + NODE_H / 2 - 6}
                    textAnchor="middle" fontSize="9.5" fill="#94a3b8" fontWeight="600">
                    {shortNum(e.runs)} run{e.runs === 1 ? '' : 's'}
                  </text>
                )}
              </g>
            );
          })}

          {/* Nodes */}
          {graph.nodes.map((n) => {
            const p = pos[n.id]; if (!p) return null;
            const pal = PALETTE[n.kind];
            const o = dim(n.id);
            const warns = warnByNode[n.id];
            const inactive = n.kind !== 'trigger' && n.active === false;
            return (
              <g key={n.id} opacity={o} style={{ cursor: 'pointer' }}
                role="button" tabIndex={0}
                aria-pressed={sel === n.id}
                aria-label={`${n.kind === 'trigger' ? 'When' : n.kind === 'condition' ? 'Only if' : 'Then'}: ${n.label}${inactive ? ' (disabled)' : ''}${warns ? ' — has a warning' : ''}`}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSel(sel === n.id ? null : n.id); if (n.ruleId && onSelectRule) onSelectRule(n.ruleId, n.source); } }}
                onClick={() => { setSel(sel === n.id ? null : n.id); if (n.ruleId && onSelectRule) onSelectRule(n.ruleId, n.source); }}>
                <rect x={p.x} y={p.y} width={NODE_W} height={NODE_H} rx={10}
                  fill={inactive ? '#f9fafb' : pal.bg}
                  stroke={sel === n.id ? pal.dot : (warns ? '#f87171' : pal.bd)}
                  strokeWidth={sel === n.id || warns ? 2 : 1}
                  strokeDasharray={inactive ? '5 4' : undefined} />
                <circle cx={p.x + 20} cy={p.y + 21} r={11} fill="#fff" stroke={pal.bd} />
                {/* Drawn rather than an icon font — the glyph must survive PDF export
                    and any browser that hasn't loaded Font Awesome yet. */}
                {n.kind === 'trigger' && (
                  <path d={`M ${p.x + 22} ${p.y + 15} L ${p.x + 16} ${p.y + 22} L ${p.x + 20} ${p.y + 22} L ${p.x + 18} ${p.y + 28} L ${p.x + 24} ${p.y + 20} L ${p.x + 20} ${p.y + 20} Z`} fill={pal.dot} />
                )}
                {n.kind === 'condition' && (
                  <g stroke={pal.dot} strokeWidth="1.6" fill="none" strokeLinecap="round">
                    <path d={`M ${p.x + 15} ${p.y + 21} L ${p.x + 19} ${p.y + 21}`} />
                    <path d={`M ${p.x + 19} ${p.y + 21} L ${p.x + 25} ${p.y + 16}`} />
                    <path d={`M ${p.x + 19} ${p.y + 21} L ${p.x + 25} ${p.y + 26}`} />
                  </g>
                )}
                {n.kind === 'action' && (
                  <path d={`M ${p.x + 17} ${p.y + 16} L ${p.x + 25} ${p.y + 21} L ${p.x + 17} ${p.y + 26} Z`} fill={pal.dot} />
                )}
                <text x={p.x + 38} y={p.y + 25} fontSize="11.5" fontWeight="700" fill={inactive ? '#6b7280' : '#111827'}>
                  {n.label.length > 24 ? `${n.label.slice(0, 23)}…` : n.label}
                </text>
                {n.kind === 'trigger' ? (
                  <text x={p.x + 38} y={p.y + 42} fontSize="9.5" fill="#6b7280">
                    {n.rules.length} rule{n.rules.length === 1 ? '' : 's'}
                  </text>
                ) : (
                  <text x={p.x + 38} y={p.y + 42} fontSize="9.5" fill="#6b7280">
                    {inactive ? 'disabled · ' : ''}
                    {(n.detail || '').length > 30 ? `${n.detail.slice(0, 29)}…` : (n.detail || (n.source === 'form' ? 'form rule' : 'job rule'))}
                  </text>
                )}
                {warns && (
                  <g>
                    <circle cx={p.x + NODE_W - 14} cy={p.y + 14} r={8} fill="#fee2e2" stroke="#f87171" />
                    <text x={p.x + NODE_W - 14} y={p.y + 18} textAnchor="middle" fontSize="10" fontWeight="900" fill="#dc2626">!</text>
                  </g>
                )}
              </g>
            );
          })}

          {/* Column captions */}
          {[['When something happens', 0], ['Only if', 1], ['Then do this', 2]].map(([label, ci]) => (
            <text key={label} x={PAD + ci * (NODE_W + GAP_X)} y={height + 26} fontSize="10" fontWeight="700"
              fill="#6b7280" letterSpacing="0.05em">{label.toUpperCase()}</text>
          ))}
        </svg>
      </div>

      <div className="cw-meta">
        Select any box (click, or Tab then Enter) to trace its path. Thicker lines have run more often; dashed boxes are disabled;
        orange loops back to a trigger the action itself fires.
      </div>
    </div>
  );
};

export default AutomationFlow;
